import './TopLinksCard.css';
import { Button, Card, CardContent, Skeleton, Typography } from '@mui/material';
import DynamicIcon from 'Components/DynamicIcon/DynamicIcon';
import { useProfile } from 'context/ProfileContext';
import { useAnalytics } from 'context/AnalyticsContext';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDashboard } from 'context/DashboardContext';
import { DashboardView } from 'utils/enums';

const VISIBLE_LINKS = 3;

const TopLinksCard = () => {
    const { t } = useTranslation();
    const { state } = useProfile();
    const { setView } = useDashboard();
    const { links: { data, loading } } = useAnalytics();

    const topLinks = useMemo(() => {
        if (!data?.length) return [];

        const profileLinks = state.currentProfile.links;
        const maxClicks = data[0]?.clicks || 1;

        return data.slice(0, VISIBLE_LINKS).map((entry) => {
            const profileLink = profileLinks.find(l => l._id === entry.linkId);
            return {
                linkId: entry.linkId,
                name: profileLink?.social_name ?? t('dashboard.main.topLinks.unknownLink'),
                iconName: profileLink?.icon_name ?? null,
                iconId: profileLink?.icon_id ?? null,
                clicks: entry.clicks,
                barWidth: Math.round((entry.clicks / maxClicks) * 100),
            };
        });
    }, [data, state.currentProfile.links, t]);

    const ghostSlots = !loading && topLinks.length > 0 ? VISIBLE_LINKS - topLinks.length : 0;

    return (
        <Card variant="outlined" className="dashboard-card top-links">
            <CardContent className="top-links-content">
                <div className="top-links-header">
                    <Typography variant="body1" component="p" className="top-links-title">
                        {t('dashboard.main.topLinks.title')}
                    </Typography>
                    {loading ? (
                        <Skeleton variant="text" width={90} height={16} />
                    ) : (
                        <Typography variant="body2" className="top-links-period">
                            {t('dashboard.main.topLinks.period')}
                        </Typography>
                    )}
                </div>

                <div className="top-links-body">
                    {loading && (
                        <>
                            {[...Array(VISIBLE_LINKS)].map((_, i) => (
                                <div key={i} className="top-links-row">
                                    <Skeleton variant="circular" width={24} height={24} />
                                    <Skeleton variant="text" width={`${60 - i * 15}%`} height={18} />
                                    <Skeleton variant="rounded" height={10} className="top-links-bar-skeleton" />
                                    <Skeleton variant="text" width={28} height={18} />
                                </div>
                            ))}
                        </>
                    )}

                    {!loading && topLinks.map((link) => (
                        <div key={link.linkId} className="top-links-row">
                            <DynamicIcon
                                size={24}
                                idIcon={link.iconId}
                                name={link.iconName}
                                isGlobal={false}
                                color="currentColor"
                                className="top-links-icon"
                            />
                            <Typography variant="body2" className="top-links-name">
                                {link.name}
                            </Typography>
                            <div className="top-links-bar-track">
                                <div
                                    className="top-links-bar-fill"
                                    style={{ width: `${link.barWidth}%` }}
                                />
                            </div>
                            <Typography variant="body1" className="top-links-clicks">
                                +{link.clicks}
                            </Typography>
                        </div>
                    ))}

                    {!loading && topLinks.length === 0 && (
                        <div className="top-links-empty">
                            <Typography variant="body2" className="top-links-empty-text">
                                {t('dashboard.main.topLinks.empty')}
                            </Typography>
                        </div>
                    )}

                    {!loading && [...Array(ghostSlots)].map((_, i) => (
                        <div key={`ghost-${i}`} className="top-links-row" style={{ opacity: 0.2 }}>
                            <Skeleton variant="circular" width={24} height={24} animation={false} />
                            <Skeleton variant="text" width={`${60 - i * 15}%`} height={18} animation={false} />
                            <Skeleton variant="rounded" height={10} animation={false} />
                            <Skeleton variant="text" width={28} height={18} animation={false} />
                        </div>
                    ))}
                </div>

                <div className="top-links-footer">
                    <Button
                        variant="outlined"
                        color="primary"
                        size="large"
                        disabled={loading}
                        onClick={() => setView(DashboardView.LINKS)}
                    >
                        {loading
                            ? <div className="btn-text-container"><Skeleton width={80} height={16} /></div>
                            : t('dashboard.main.topLinks.viewAll')}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};

export default TopLinksCard;
